import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "../../api.js";
import { parsePnXml } from "../../../../lib/pn/parse.js";
import { pnFormHtml } from "../../../../lib/pn/form.js";
import { encodeCp1251 } from "../../../../lib/pn/cp1251.js";
import formCssUrl from "../../../../lib/pn/form.css?url";
import PnSign from "./PnSign.jsx";
import { splitStyles, applyStyles, openStyled } from "./cspHtml.js";

// Крок 4: податкова накладна на бланку затвердженої форми — обов'язково перед підписом.
// Бланк будується з того самого XML, хеш якого фіксується («я переглянув»). Хеш рахуємо і тут, у
// браузері, з показаного тексту: якщо він не збігся з серверним, переглядати нема чого — показуємо помилку.
const hex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
const FORM_W = 1100; // ширина бланка (A4 альбомна) у px — масштабуємо під екран

export default function PnForm({ companyId, draftId, company, docRes, onReviewed, onBack, onRelogin }) {
  const [tab, setTab] = useState("ours");
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [localHash, setLocalHash] = useState(null);
  const [signKey, setSignKey] = useState(0), [locked, setLocked] = useState(false), [started, setStarted] = useState(false), [dpsBusy, setDpsBusy] = useState(false), [dpsErr, setDpsErr] = useState(null);
  const d = docRes.data;
  const parsed = useMemo(() => (d?.xml ? parsePnXml(d.xml) : null), [d?.xml]);
  // Стилі бланка — файлом із нашого origin (CSP забороняє вбудовані); абсолютна адреса — щоб працювало й у вікні blob:
  const html = useMemo(() => (parsed ? pnFormHtml(parsed, { cssHref: new URL(formCssUrl, location.href).href }) : ""), [parsed]);

  useEffect(() => {
    setChecked(false); setLocalHash(null);
    if (!d?.xml) return;
    crypto.subtle.digest("SHA-256", encodeCp1251(d.xml).bytes).then((b) => setLocalHash(hex(b)));
  }, [d?.xml]);

  if (docRes.pending) return <p className="muted" role="status">Формуємо податкову накладну…</p>;
  if (docRes.error) return <p className="error" role="alert">{docRes.error.message}</p>;
  if (!d.xml) return (
    <div className="card warn" role="alert"><b>Документ ще має помилки.</b> Поверніться до перевірки й виправте їх — тоді покажемо бланк.
      <div className="actions" style={{ marginTop: 12 }}><button type="button" className="btn btn-secondary btn-sm" onClick={onBack}>← До перевірки</button></div></div>
  );
  const hashOk = localHash && localHash === d.sha256;
  const reviewed = d.reviewed?.sha256 === d.sha256;

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await api(`/api/companies/${companyId}/pn/drafts/${draftId}/review`, { method: "POST", body: { sha256: d.sha256, version: d.version } });
      onReviewed();
    } catch (e) {
      setError(e instanceof ApiError && e.status === 409 ? new Error("Документ змінився після показу бланка. Ми показали новий бланк — перегляньте його ще раз.") : e);
      if (e.status === 409) onReviewed();
    } finally { setBusy(false); }
  };

  return (
    <div className="pn-form">
      <div className="pn-form-bar">
        <div className="tabs" role="tablist" aria-label="Подання документа">
          <button type="button" role="tab" id="pn-tab-ours" aria-selected={tab === "ours"} aria-controls="pn-panel-ours" className={tab === "ours" ? "tab active" : "tab"} onClick={() => setTab("ours")}>Податкова накладна</button>
          <button type="button" role="tab" id="pn-tab-dps" aria-selected={tab === "dps"} aria-controls="pn-panel-dps" className={tab === "dps" ? "tab active" : "tab"} onClick={() => setTab("dps")}>Бланк із ДПС</button>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => openFull(html)}>Відкрити на весь екран</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => printForm(html)}>Друк / PDF</button>
          <a className="btn btn-secondary btn-sm" href={`/api/companies/${companyId}/pn/drafts/${draftId}/xml`}>Завантажити XML</a>
        </div>
      </div>

      {tab === "ours" ? (
        <section id="pn-panel-ours" role="tabpanel" aria-labelledby="pn-tab-ours">
          {started ? <p className="muted small">Нижче — накладна в тому вигляді, в якому її зберіг кабінет ДПС: підписується саме вона.</p> : (<>
            <ScaledForm html={html} />
            <TextVersion parsed={parsed} />
          </>)}
        </section>
      ) : (
        <section id="pn-panel-dps" role="tabpanel" aria-labelledby="pn-tab-dps" className="card">
          <h3>Бланк, який сформує ДПС</h3>
          <p>Щоб отримати бланк із ДПС, у кабінеті створюється чернетка цієї накладної (без підпису й без відправлення).
            Робимо це лише на вашу команду — переглядати локальний бланк можна й без цього.</p>
          {dpsErr && <p className="error" role="alert">{dpsErr.message}</p>}
          <button type="button" className="btn btn-secondary btn-sm" disabled={!reviewed || dpsBusy || !["submitter", "owner"].includes(company.role)} aria-describedby="pn-dps-later"
            onClick={async () => { setDpsBusy(true); setDpsErr(null); try { await api(`/api/companies/${companyId}/pn/drafts/${draftId}/submission`, { method: "POST" }); setSignKey((k) => k + 1); setTab("ours"); } catch (e) { setDpsErr(e); } finally { setDpsBusy(false); } }}>
            {dpsBusy ? "Зберігаємо в кабінеті ДПС…" : "Зберегти проект ПН і отримати бланк ДПС"}</button>
          <p className="muted small" id="pn-dps-later">{reviewed ? "Повторне натискання не створює другу чернетку." : "Доступно після підтвердження перегляду накладної."}</p>
        </section>
      )}

      <div className="card pn-confirm">
        <p className="small muted">Документ {parsed.cdoc} · {d.size} байт · SHA-256 <span className="mono-sm">{d.sha256.slice(0, 12)}…{d.sha256.slice(-6)}</span>
          {hashOk ? " · збігається з показаним бланком" : localHash ? "" : " · перевіряємо…"}</p>
        {localHash && !hashOk && <p className="error" role="alert">Показаний бланк не відповідає документу на сервері. Оновіть сторінку — без цього підтвердити перегляд не можна.</p>}
        {reviewed ? (
          <p className="pn-ok" role="status">✓ Податкову накладну переглянуто{d.reviewed?.at ? ` ${new Date(d.reviewed.at).toLocaleString("uk-UA")}` : ""}. Будь-яка зміна даних скасує це підтвердження.</p>
        ) : (
          <label className="check"><input type="checkbox" checked={checked} disabled={!hashOk} onChange={(e) => setChecked(e.target.checked)} />
            Я переглянув(-ла) податкову накладну, дані правильні</label>
        )}
        {error && <p className="error" role="alert">{error.message}</p>}
        <div className="actions">
          {!locked && <button type="button" className="btn btn-secondary" onClick={onBack}>← Змінити дані</button>}
          {!reviewed && <button type="button" className="btn btn-secondary" disabled={!checked || !hashOk || busy} onClick={confirm}>{busy ? "Зберігаємо…" : "Підтвердити перегляд"}</button>}
          {!reviewed && <button type="button" className="btn btn-secondary" disabled aria-describedby="pn-sign-later">Зберегти проект ПН</button>}
          {!reviewed && <button type="button" className="btn" disabled aria-describedby="pn-sign-later">Підписати й зареєструвати</button>}
        </div>
        {!reviewed && <p className="muted small" id="pn-sign-later">«Зберегти проект ПН» (чернетка в кабінеті ДПС) і підпис стануть доступні після підтвердження перегляду цього бланка.</p>}
        {reviewed && <PnSign key={signKey} companyId={companyId} draftId={draftId} company={company} ourXml={d.xml} onRelogin={onRelogin} onLocked={() => setLocked(true)} onStarted={setStarted} />}
      </div>
    </div>
  );
}

// Бланк в iframe (ізольовані стилі форми), масштабований під ширину контейнера
export function ScaledForm({ html, width = FORM_W, title = "Податкова накладна — бланк затвердженої форми" }) {
  const box = useRef(null), frame = useRef(null);
  const [scale, setScale] = useState(1), [h, setH] = useState(800);
  useEffect(() => {
    const fit = () => setScale(Math.min(1, (box.current?.clientWidth || width) / width));
    fit();
    const ro = new ResizeObserver(fit); ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const parts = useMemo(() => splitStyles(html), [html]);
  const onLoad = () => {
    const doc = frame.current?.contentDocument;
    applyStyles(doc, parts.css);
    // Висота за вмістом body: documentElement.scrollHeight не зменшується, коли новий документ коротший за фрейм
    if (doc?.body) setH(Math.ceil(doc.body.getBoundingClientRect().height) + 24);
  };
  return (
    <div className="pn-sheet" ref={box} style={{ height: h * scale }}>
      <iframe ref={frame} title={title} srcDoc={parts.html} onLoad={onLoad} sandbox="allow-same-origin allow-modals"
        style={{ width, height: h, transform: `scale(${scale})` }} />
    </div>
  );
}

// Стилі бланка — через CSSOM (cspHtml.js): CSP сторінки блокує <style> і style="…" у srcdoc / about:blank / blob:
function openFull(html) { openStyled(html); }
function printForm(html) {
  const f = document.createElement("iframe"), { html: body, css } = splitStyles(html);
  Object.assign(f.style, { position: "fixed", width: "0", height: "0", border: "0" });
  f.srcdoc = body;
  f.onload = () => { applyStyles(f.contentDocument, css); f.contentWindow.print(); setTimeout(() => f.remove(), 1000); };
  document.body.append(f);
}

// Текстова версія тих самих полів — для читачів екрана й телефона
export function TextVersion({ parsed }) {
  const f = parsed.fields, ua = (s) => (s?.length === 8 ? `${s.slice(0, 2)}.${s.slice(2, 4)}.${s.slice(4)}` : s);
  return (
    <details className="card pn-text">
      <summary>Текстова версія накладної</summary>
      <dl className="pairs">
        <div><dt>Податкова накладна</dt><dd>№ {f.HNUM} від {ua(f.HFILL)}</dd></div>
        <div><dt>Продавець</dt><dd>{f.HNAMESEL}, ІПН {f.HKSEL}, код {f.HTINSEL}</dd></div>
        <div><dt>Покупець</dt><dd>{f.HNAMEBUY}, ІПН {f.HKBUY}{f.HTINBUY ? `, код ${f.HTINBUY}` : ""}{f.HORIG1 ? `; не підлягає наданню покупцю, тип причини ${f.HTYPR}` : ""}</dd></div>
        <div><dt>Разом з ПДВ</dt><dd>{f.R04G11} грн</dd></div>
        <div><dt>ПДВ</dt><dd>{f.R03G11} грн</dd></div>
        <div><dt>Посадова особа</dt><dd>{f.HBOS}, РНОКПП {f.HKBOS}</dd></div>
      </dl>
      <ol className="pn-text-rows">
        {parsed.rows.map((r) => (
          <li key={r.n}>{r.G3S}: {r.G5} {r.G4S} × {r.G6} = {r.G010} грн без ПДВ, ставка {r.G008}, ПДВ {r.G11_10}; код {r.G4 || r.G33}{r.G32 ? ", імпорт" : ""}</li>
        ))}
      </ol>
    </details>
  );
}
